// src/components/cooler/inventory/components/ItemDetails/EditForm.tsx

import React, { useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  FormControlLabel,
  Switch,
  InputAdornment,
  Paper,
  Typography,
  Collapse,
  Fade,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Tag as CategoryIcon,
  MapPin as LocationIcon,
  Calendar,
  Building as BusinessIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Circle as UnfilledIcon,
  Power,
} from 'lucide-react';

import { 
  CATEGORIES, 
  LOCATIONS, 
  SECOND_LOCATIONS,
  VENDORS,
  shouldShowSerialNumber,
  type SecondLocation,
} from '../../../constants/inventoryConstants';
import { hasSecondLocation } from '../../../utils/locationHelpers';
import type { InventoryItem } from '../../../types/inventoryTypes';

interface EditFormProps {
  item: InventoryItem;
  onChange: (item: InventoryItem) => void;
}

const SectionHeader: React.FC<{ 
  icon: React.ReactNode; 
  title: string; 
  completed?: boolean;
  onClick?: () => void;
  collapsible?: boolean;
}> = ({ 
  icon, 
  title, 
  completed = false,
  onClick,
  collapsible = false
}) => (
  <Box sx={{ 
    display: 'flex', 
    alignItems: 'center', 
    gap: 1, 
    mb: 2, 
    p: 1,
    borderRadius: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
    cursor: collapsible ? 'pointer' : 'default',
    userSelect: 'none',
    '&:hover': collapsible ? {
      backgroundColor: 'rgba(0, 0, 0, 0.04)'
    } : {}
  }}
  onClick={onClick}
  >
    {icon}
    <Typography variant="subtitle2" sx={{ fontWeight: 600, flexGrow: 1 }}>
      {title}
    </Typography>
    {completed ? (
      <CheckCircleIcon size={20} color="green" />
    ) : (
      <UnfilledIcon size={20} color="gray" />
    )}
  </Box>
);

const EditForm: React.FC<EditFormProps> = ({ item, onChange }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showTechnical, setShowTechnical] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Helper to check completion status
  const isBasicComplete = Boolean(item.name && item.category);
  const isLocationComplete = Boolean(item.primaryLocation);
  const hasPurchaseInfo = Boolean(item.purchaseDate || item.vendor || item.purchaseValue);
  const hasTechnicalInfo = Boolean(item.serialNumber || item.requiresPower || item.tags?.length);

  // Auto-uppercase handler for serial numbers
  const handleSerialNumberChange = (value: string) => {
    onChange({ ...item, serialNumber: value.toUpperCase() });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: isMobile ? 1 : 0 }}>
      {/* Basic Information Section */}
      <Paper sx={{ p: isMobile ? 2 : 3, backgroundColor: 'rgba(25, 118, 210, 0.02)' }}>
        <SectionHeader 
          icon={<InfoIcon size={20} />} 
          title="Basic Information" 
          completed={isBasicComplete}
        />
        
        <Grid container spacing={isMobile ? 2 : 3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Equipment Name"
              placeholder="e.g., Shure SM58 Dynamic Microphone"
              value={item.name}
              onChange={(e) => onChange({ ...item, name: e.target.value })}
              helperText="What should we call this equipment?"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>Category</InputLabel>
              <Select
                value={item.category}
                label="Category"
                onChange={(e) => onChange({ ...item, category: e.target.value as typeof CATEGORIES[number] })}
                startAdornment={
                  <InputAdornment position="start">
                    <CategoryIcon size={20} />
                  </InputAdornment>
                }
              >
                {CATEGORIES.map((cat) => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={isMobile ? 2 : 3}
              label="Description"
              placeholder="Describe the equipment's purpose and key features"
              value={item.description}
              onChange={(e) => onChange({ ...item, description: e.target.value })}
              helperText="Help others understand what this equipment does"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Notes"
              placeholder="Any special handling instructions or additional information"
              value={item.notes}
              onChange={(e) => onChange({ ...item, notes: e.target.value })}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Location Section - Progressive reveal */}
      <Fade in={isBasicComplete}>
        <Paper sx={{ p: isMobile ? 2 : 3, backgroundColor: 'rgba(76, 175, 80, 0.02)' }}>
          <SectionHeader 
            icon={<LocationIcon size={20} />} 
            title="Location Information" 
            completed={isLocationComplete}
          />
          
          <Grid container spacing={isMobile ? 2 : 3}>
            <Grid item xs={12} md={hasSecondLocation(item.primaryLocation) ? 6 : 8}>
              <FormControl fullWidth required>
                <InputLabel>Where will this be stored?</InputLabel>
                <Select
                  value={item.primaryLocation}
                  label="Where will this be stored?"
                  onChange={(e) => onChange({ ...item, primaryLocation: e.target.value as typeof LOCATIONS[number] })}
                  startAdornment={
                    <InputAdornment position="start">
                      <LocationIcon size={20} />
                    </InputAdornment>
                  }
                >
                  {LOCATIONS.map((loc) => (
                    <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Second Location - Progressive reveal */}
            {hasSecondLocation(item.primaryLocation) && (
              <Fade in={true}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Shelf/Drawer</InputLabel>
                    <Select
                      value={item.secondLocation || ''}
                      label="Shelf/Drawer"
                      onChange={(e) => onChange({
                        ...item,
                        secondLocation: e.target.value as SecondLocation
                      })}
                    >
                      <MenuItem value="">
                        <em>None specified</em>
                      </MenuItem>
                      {SECOND_LOCATIONS.map((loc: SecondLocation) => (
                        <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Fade>
            )}

            <Grid item xs={12} md={hasSecondLocation(item.primaryLocation) ? 12 : 4}>
              <FormControl fullWidth>
                <InputLabel>Alternative Location</InputLabel>
                <Select
                  value={item.secondaryLocation || ''}
                  label="Alternative Location"
                  onChange={(e) => onChange({ ...item, secondaryLocation: e.target.value as typeof LOCATIONS[number] | undefined })}
                >
                  <MenuItem value="N/A">N/A</MenuItem>
                  {LOCATIONS.map((loc) => (
                    <MenuItem 
                      key={loc} 
                      value={loc}
                      disabled={loc === item.primaryLocation}
                    >
                      {loc}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
      </Fade>

      {/* Purchase Information Section - Collapsible */}
      {isLocationComplete && (
        <Paper sx={{ p: isMobile ? 2 : 3, backgroundColor: 'rgba(255, 152, 0, 0.02)' }}>
          <SectionHeader 
            icon={<BusinessIcon size={20} />} 
            title="Purchase Information (Optional)" 
            completed={hasPurchaseInfo}
            onClick={() => setShowAdvanced(!showAdvanced)}
            collapsible={true}
          />
          
          <Collapse in={showAdvanced}>
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={isMobile ? 2 : 3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Purchase Date"
                    value={item.purchaseDate}
                    onChange={(e) => onChange({ ...item, purchaseDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Calendar size={20} />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    freeSolo
                    options={VENDORS}
                    value={item.vendor || ''}
                    onChange={(_, newValue) => onChange({ 
                      ...item, 
                      vendor: newValue as typeof VENDORS[number] | undefined 
                    })}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Vendor" 
                        placeholder="Who did you buy this from?"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <BusinessIcon size={20} />
                            </InputAdornment>
                          )
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Purchase Value"
                    placeholder="0.00"
                    value={item.purchaseValue || ''}
                    onChange={(e) => onChange({ 
                      ...item, 
                      purchaseValue: parseFloat(e.target.value),
                      isInsured: parseFloat(e.target.value) >= 500
                    })}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">€</InputAdornment>
                    }}
                    helperText="Items over €500 are automatically insured"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Current Value"
                    placeholder="0.00"
                    value={item.currentValue || ''}
                    onChange={(e) => onChange({ ...item, currentValue: parseFloat(e.target.value) })}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">€</InputAdornment>
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  {(item.purchaseValue || 0) >= 500 && (
                    <Box sx={{ pt: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={item.isInsured}
                            onChange={(e) => onChange({ ...item, isInsured: e.target.checked })}
                          />
                        }
                        label="Insurance Coverage"
                      />
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Paper>
      )}

      {/* Technical Specifications - Collapsible */}
      {isLocationComplete && (
        <Paper sx={{ p: isMobile ? 2 : 3, backgroundColor: 'rgba(156, 39, 176, 0.02)' }}>
          <SectionHeader 
            icon={<SettingsIcon size={20} />} 
            title="Technical Specifications (Optional)" 
            completed={hasTechnicalInfo}
            onClick={() => setShowTechnical(!showTechnical)}
            collapsible={true}
          />
          
          <Collapse in={showTechnical}>
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={isMobile ? 2 : 3}>
                {/* Serial Number - Progressive reveal based on category */}
                {shouldShowSerialNumber(item.category) && (
                  <Fade in={true}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Serial Number"
                        placeholder="Enter serial number"
                        value={item.serialNumber || ''}
                        onChange={(e) => handleSerialNumberChange(e.target.value)}
                        helperText="Automatically converted to uppercase"
                        sx={{ 
                          '& .MuiInputBase-input': {
                            fontFamily: 'monospace',
                            textTransform: 'uppercase'
                          }
                        }}
                      />
                    </Grid>
                  </Fade>
                )}

                {/* Power Requirements */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ pt: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={item.requiresPower}
                          onChange={(e) => onChange({ ...item, requiresPower: e.target.checked })}
                        />
                      }
                      label="Requires Power Connection"
                    />
                  </Box>
                </Grid>

                {/* Power Location - Progressive reveal */}
                {item.requiresPower && (
                  <Fade in={true}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Power Source Location</InputLabel>
                        <Select
                          value={item.powerLocation || ''}
                          label="Power Source Location"
                          onChange={(e) => onChange({ ...item, powerLocation: e.target.value as typeof LOCATIONS[number] | undefined })}
                          startAdornment={
                            <InputAdornment position="start">
                              <Power size={20} />
                            </InputAdornment>
                          }
                        >
                          {LOCATIONS.map((loc) => (
                            <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Fade>
                )}

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Quantity"
                    value={item.quantity}
                    onChange={(e) => onChange({ ...item, quantity: parseInt(e.target.value) })}
                    inputProps={{ min: 1 }}
                    helperText="How many of this item do you have?"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Tags"
                    placeholder="wireless, backup, stage-left, etc."
                    value={item.tags?.join(', ') || ''}
                    onChange={(e) => onChange({
                      ...item,
                      tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag)
                    })}
                    helperText="Separate tags with commas - helps with searching later"
                  />
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Paper>
      )}
    </Box>
  );
};

export default EditForm;